/** Число значащих цифр для визуального отображения расчётных величин. */
export const SIGNIFICANT_FIGURES = 3;

/** Plotly D3-формат: 3 значащие цифры без лишней дробной части. */
export const PLOTLY_SIGNIFICANT_HOVERFORMAT = '.3g';

/**
 * Округляет значение до заданного числа значащих цифр для отображения.
 * Не использует экспоненциальную запись в типичном клиническом диапазоне.
 *
 * @param {unknown} value
 * @param {number} [digits=3]
 * @returns {string}
 */
export function formatSignificantFigures(value, digits = SIGNIFICANT_FIGURES) {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '';
  if (n === 0) return '0';

  const rounded = Number(n.toPrecision(digits));
  if (rounded === 0) return '0';

  const abs = Math.abs(rounded);
  const exponent = Math.floor(Math.log10(abs));
  const decimalPlaces = Math.max(0, digits - 1 - exponent);
  let formatted = rounded.toFixed(decimalPlaces);
  if (formatted.includes('.')) {
    formatted = formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }
  return formatted;
}

/**
 * То же, что {@link formatSignificantFigures}, но пустые и нечисловые значения → «—».
 *
 * @param {unknown} value
 * @param {string} [empty='—']
 * @returns {string}
 */
export function formatSignificantFiguresOrDash(value, empty = '—') {
  if (value === null || value === undefined || value === '') return empty;
  const formatted = formatSignificantFigures(value);
  if (formatted !== '') return formatted;
  if (typeof value === 'string' && value.trim() !== '') return value;
  return empty;
}

/**
 * Интервал (например CI95%) с округлением каждой границы.
 *
 * @param {unknown} a
 * @param {unknown} b
 * @param {string} [separator=' – ']
 * @returns {string}
 */
export function formatSignificantFiguresRange(a, b, separator = ' – ') {
  if (a == null && b == null) return '—';
  return `${formatSignificantFiguresOrDash(a)}${separator}${formatSignificantFiguresOrDash(b)}`;
}

/**
 * Добавляет hoverformat с 3 значащими цифрами к осям Plotly, не перезаписывая уже заданный формат.
 *
 * @param {object|null|undefined} layout
 * @returns {object}
 */
export function withSignificantFigureHover(layout = {}) {
  const applyAxis = (axis) => {
    const ax = axis && typeof axis === 'object' ? { ...axis } : {};
    if (!ax.hoverformat) ax.hoverformat = PLOTLY_SIGNIFICANT_HOVERFORMAT;
    return ax;
  };
  return {
    ...layout,
    xaxis: applyAxis(layout.xaxis),
    yaxis: applyAxis(layout.yaxis),
  };
}
